import { Box, Grid, Link, Tooltip, Typography } from '@mui/material';
import { green } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory, useLocation } from 'react-router';

import { DataAPIContext } from '../api';
import { DELETE, GET_ONE } from '../api/dataProvider';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import { gridLoadingSlots } from '../components/GridLoadingOverlay';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ReactComponent as GoToChatIcon } from '../icons/go_to_chat_link.svg';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import ReviewListToolbar, { DEFAULT_TRAVEL_DATE_PRESET, travelDatePresetAfter } from './ReviewListToolbar';
import { ConfirmationDialogText } from '../settings/utils';
import WhiteBackgroundTooltip from '../components/WhiteTooltip';
import { ReviewModal, CustomTooltip } from './ReviewDetails';
import { positiveReviewLink } from './reviewLink';
import ReviewInsights from './ReviewInsights';
import { DEFAULT_REVIEW_PERIOD, periodFilters, periodRange } from './reviewInsightsUtils';
import { canSeeReviewInsights } from './reviewInsightsAccess';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: '#FCFCFE',
        paddingBottom: '1rem !important',
    },
    recordListWrapper: {

        [theme.breakpoints.down('sm')]: {
            margin: '0 1rem !important',
        },
    },
    recordList: {
        border: 'none !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        height: `calc(${theme.cp.contentHeight} - 11.5rem) !important`,
        // Multi-line rows: top-align cell content when row height grows (e.g. long tour names)
        '& .MuiDataGrid-row--dynamicHeight .MuiDataGrid-cell': {
            alignItems: 'flex-start',
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
        },
    },
    // No "0–0 of 0" under the loading overlay before the first page arrives (Ben, 2026-10-29), and
    // the old page's count faded with its rows while the next one loads
    recordListFirstLoad: {
        '& .MuiTablePagination-root': { visibility: 'hidden' },
    },
    recordListReloading: {
        '& .MuiTablePagination-root': { opacity: 0.5 },
    },
    listItem: {},
    // Keeps long wrapped text inside the flex column so it does not draw over the Action column
    reviewColumnCell: {
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
    },
    dm_enabled: {
        color: green[800],
        fontWeight: 'bold',
    },
    dm_disabled: {
        textDecoration: 'line-through',
    },
    mainContainer: {
        margin: '1rem !important', border: 'solid 1px #D8D8D8 !important', borderRadius: '12px !important'
        ,
        [theme.breakpoints.down('sm')]: {
            margin: '0rem !important', border: 'none !important', borderRadius: '12px !important'
        },

    }
}));


const REVIEW_FILTER_FIELDS = ['rating', 'tour_id', 'after', 'before'];

const REVIEW_SLOTS = gridLoadingSlots('Loading reviews…');

// The grid's filters from the URL. A link without any (the menu, a bookmark) opens on the default Travel Date
// preset, except with the charts, whose period is the review date. "All time" is in the URL as an empty `after`
// (see the URL effect), so it survives a reload and a shared link.
export const initialReviewFilters = (search, showInsights) => {
    const params = new URLSearchParams(search);
    const filters = [];
    for (const [key, value] of params.entries()) {
        if (key === 'query' || key === 'tab' || key === 'period') {
            continue;
        }
        filters.push(JSON.parse(value));
    }
    if (filters.length === 0 && !showInsights) {
        filters.push({ field: 'after', value: travelDatePresetAfter(DEFAULT_TRAVEL_DATE_PRESET) });
    }
    REVIEW_FILTER_FIELDS.forEach((field) => {
        if (!filters.some((filter) => filter.field === field)) {
            filters.push({ field, value: null });
        }
    });
    return filters;
};

const ReviewList = (props) => {
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'review';
    const classes = useStyles();
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [lastUpdate, setLastUpdate] = React.useState();
    const [syncActive, setSyncActive] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm');
    const { enqueueSnackbar } = useSnackbar();
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const { roleMap, tourOperator, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    const [relatedLoading] = React.useState(false);
    // The grid's own loading flag (every fetch: filters, page, sort), for the bar at the top and the
    // pagination count; the grid shows REVIEW_SLOTS' overlay itself
    const [listLoading, setListLoading] = React.useState(true);
    const [listAnswered, setListAnswered] = React.useState(false);
    const onListLoading = React.useCallback((value) => {
        setListLoading(value);
        if (!value) setListAnswered(true);
    }, []);
    const location = useLocation();
    // From the URL before the first render, '' when it has none: the toolbar reports '' on mount, and
    // a change from null fetched the list and the charts' summary again (2026-10-29).
    const [query, setQuery] = React.useState(() => new URLSearchParams(location.search).get('query') || '');
    // CP-5220: the charts above the table, and the review-date period that narrows both
    const showInsights = canSeeReviewInsights(tourOperator);
    const [period, setPeriod] = React.useState(() => new URLSearchParams(location.search).get('period') || DEFAULT_REVIEW_PERIOD);
    const periodRangeValue = React.useMemo(() => periodRange(period), [period]);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectedAspects, setSelectedAspects] = React.useState([]);
    const reviewRatingSystem = +(tourOperator?.config?.review_rating_system || 5);
    const reviewThreshold = +(tourOperator?.config?.reviews_low_high_threshold || (reviewRatingSystem === 10 ? 8 : 4));

    const handleOpenModal = (aspects) => {
        setSelectedAspects(aspects);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };
    const [recordListFilters, setRecordListFilters] = React.useState(() => initialReviewFilters(location.search, showInsights));
    const listFilters = React.useMemo(
        () => (showInsights ? [...recordListFilters, ...periodFilters(periodRangeValue)] : recordListFilters),
        [showInsights, recordListFilters, periodRangeValue]
    );
    // The charts follow every filter but Rating: picking "5" would leave one bar in the score breakdown
    const chartFilters = React.useMemo(() => recordListFilters.filter((f) => f.field !== 'rating'), [recordListFilters]);

    const renderRatingValue = React.useCallback((row, size = 50) => {
        if (+row.review_rating_system === 10) {
            const ratingValue = Number(row.star_rating);
            const rowThreshold = Number(row.low_high_threshold) || reviewThreshold;
            const isPositiveRating = ratingValue >= rowThreshold;

            return (
                <Box
                    component="span"
                    sx={{
                        width: size,
                        height: size,
                        borderRadius: '10px',
                        border: `1px solid ${isPositiveRating ? '#CFE7D9' : '#EAC7CD'}`,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#17325C',
                        fontWeight: 600,
                        fontSize: size > 40 ? 20 : 16,
                        backgroundColor: isPositiveRating ? '#E4F4EA' : '#F8DDE1',
                    }}
                >
                    {ratingValue}
                </Box>
            );
        }

        return <img style={{ width: size, height: size, marginTop: size <= 35 ? '0.15rem' : 0 }} alt={`Rating ${Math.floor(row.star_rating)}`} src={`/rating-emojis/rating-${Math.floor(row.star_rating)}.svg`} />;
    }, [reviewThreshold]);


    // Update the URL whenever the filters or query change
    React.useEffect(() => {
        const params = new URLSearchParams();
        let filters = [...recordListFilters]
        filters.forEach((filter, index) => {
            // an empty `after` too: it is "All time", where a missing one is the default preset
            if (filter.value || filter.field === 'after') {
                params.append(index, JSON.stringify(filter));
            }
        });

        // Add the query to the URL parameters
        if (query) {
            params.append('query', query);
        }

        params.set('tab', 'reviews');
        if (showInsights) {
            params.set('period', period);
        }

        history.push({
            pathname: location.pathname,
            search: params.toString(),
        });
    }, [recordListFilters, query, period, showInsights]);

    // The filters and query are read from the URL in their initial state (above). This used to set
    // them again in a mount effect, with a new `lastUpdate`, and the list fetched a second time.


    const render_user_name = (row) => {
        const user = row.participant.user;
        if (!user) return <Typography></Typography>;
        const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || '';
        return <Typography>{fullName}</Typography>;
    };

    const ListItemRenderer = (row, idx, rows, gridApi) => {
        let chatChannelID = row.associated_twilio_conversation_unique_name?.split('::')[1];
        const commentAspect = row.data?.find(item => 
            item.aspects?.some(aspect => aspect.type === "Comment" && aspect.comment)
        );
        const comment = row.data && row.data.length > 0 ? commentAspect?.aspects?.find(aspect => (aspect.type === "Comment" || aspect.type_id === 3))?.comment : row.comment;
        const aspects = row.data?.flatMap(item => item.aspects || []);
        const noBorder = idx === rows.length - 1;

       return(
            <Grid container key={idx} style={{ borderBottom: noBorder ? 'none' : "1px solid #D0D5DD" }}>
                <Grid item xs={12} container alignItems="center" justifyContent='space-between'>
                    <Grid item xs={'auto'} container alignItems="center" justifyContent='flex-start'>
                        <Grid item px={1}>
                            {renderRatingValue(row, 35)}
                        </Grid>
                        <Grid item>
                            <Link href={"/trip/" + row.participant.tour.id} style={{ textDecoration: 'none' }}> <Typography fontWeight={500} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.participant.tour.name}</Typography></Link>
                        </Grid>
                    </Grid>
                    <Grid item pr={1}   alignItems="center">
                        {row.associated_twilio_conversation_unique_name && row.associated_twilio_conversation_non_guest_participants_state != 'deleted' && <GoToChatIcon style={{ cursor: 'pointer', marginBottom: '0.25rem' }} onClick={() => history.push('/chat/' + chatChannelID + '/conversations/' + row.associated_twilio_conversation_unique_name + '/interactivechat')} />}
                    </Grid>
                </Grid>
                <Grid item xs={12} container gap={1} pl={2}>
                    <Grid item>
                        <Typography fontSize="12px" >{moment.utc(row.participant.start_ts).format('MMM DD, YYYY')}</Typography>
                    </Grid>
                    {moment.utc(row.participant.end_ts).format('MMM DD, YYYY') !== moment.utc(row.participant.start_ts).format('MMM DD, YYYY') &&
                    <Grid>
                        <Typography fontSize="12px" >
                            {moment.utc(row.participant.end_ts).format('MMM DD, YYYY')}
                        </Typography>
                    </Grid>}
                </Grid>
                <Grid item xs={12} px={2} py={1} gap={2} container alignItems="center">
                    <Grid item >
                        <Typography style={{ overflow: 'scroll', maxWidth:'150px',whiteSpace: 'nowrap' }} color="primary" onClick={() => history.push(`/user/${row.participant.user.id}`)}>
                            {render_user_name(row)}
                        </Typography>
                    </Grid>
                    <Grid item >
                        <Typography fontSize="12px">{moment(row.updated_ts).format('MMM DD, YYYY')} • {moment(row.updated_ts).format('hh:mm A')}</Typography>
                    </Grid>
                </Grid>
                <Box component="div" px={2} py={1} display="flex" flexDirection="column" alignItems="stretch" gap={0.5}>
                    {comment && (
                        <Typography sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{comment}</Typography>
                    )}
                    {(() => {
                        const listAspects = aspects ? aspects : (comment ? [{ type: 'Comment', comment }] : []);
                        const hasRatingAspects = listAspects.some((a) => a.type === 'Rating');
                        const showDetails = hasRatingAspects || (listAspects.length > 1);
                        if (!showDetails) return null;
                        return (
                            <Typography
                                color="primary"
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleOpenModal(listAspects)}
                            >
                                See more
                            </Typography>
                        );
                    })()}
                </Box>
            </Grid>)
    };

    const doDeleteReviewList = (idlist) => {
        setSyncActive(true);
        dataAPI(DELETE, resource, { id: idlist })
            .then((res) => {
                console.log('delete res', res);
                enqueueSnackbar('Review deleted', { variant: 'default' });
                setLastUpdate(new Date().toString());
                setIdsToDelete(null);
            })
            .catch((err) => {
                enqueueSnackbar('Error deleting review', { variant: 'error' });
            })
            .finally(() => {
                setSelectedIds([]);
                setSyncActive(false);
            });
    };

    const onExportList = async () => {
        try {
            setSyncActive(true);
            const params = {
                filter: {
                    filters: JSON.stringify(listFilters),
                },
                urlSuffix: "export/",
            };
            console.log('recordList', params);

            console.log('onExportList: requesting data', params);
            const res = await dataAPI(GET_ONE, resource, params);
            const body = await res.response.blob();
            console.log('onExportList: got response', body);
            const url = window.URL.createObjectURL(body);

            var filename = "";
            const disposition = res.headers['Content-Disposition'];
            if (disposition && disposition.indexOf('attachment') !== -1) {
                var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                var matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) { 
                    filename = matches[1].replace(/['"]/g, '');
                }
            }
            if (!filename || filename.length <= 0) {
                filename = "user_reviews.csv";
            }
            console.log('onExportList: downloading as file', filename, url);

            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("downlo: Error exporting list:", error);
        } finally {
            setSyncActive(false);
        }
    }

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_review':
            case 'delete_Reviews':
                doDeleteReviewList(idsToDelete);
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const record_list_cols = [
        ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
        {
            field: 'date',
            headerName: 'Date',
            sortable: true,
            width: 125,
            renderCell: (p) => <>{moment(p.row.updated_ts).format('MMM DD, YYYY')}</>,
        },
        {
            field: 'time',
            headerName: 'Time',
            sortable: true,
            width: 100,
            renderCell: (p) => <>{moment(p.row.updated_ts).format('HH:mm A')}</>,
        },
        {
            field: 'rating',
            headerName: 'Rating',
            sortable: true,
            width: 75,
            renderCell: (p) => renderRatingValue(p.row, 50),
        },
        {
            field: 'Tour',
            headerName: 'Tour',
            sortable: true,
            flex: 0.6,
            renderCell: (p) => (
                <Tooltip title={p.row.participant.tour.name + ([...p.row.conversation_tags || [], ...p.row.internal_tags || []] ? " " : "") + [...(p.row.conversation_tags || []), ...(p.row.internal_tags || [])]?.map(x => x.data?.friendly_name || x.name).join(", ")}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35 }}>
                        <Link href={"/trip/" + p.row.participant.tour.id} style={{ textDecoration: 'none', minWidth: 0 }}><Typography component="span" fontWeight={500} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{p.row.participant.tour.name}</Typography></Link>
                        {
                            (p.row.conversation_tags || []).map((tag, idx) => (tag.category !== 'enter_excluded_tags_here') &&
                                <span key={`conv-${tag.name}-${idx}`} style={{ fontSize: ".88rem", padding: "2px 4px 2px 4px", color: "black", background: tag.category === 'departure' ? "#EAF4FF" : "#FDF3E1", borderRadius: 3, lineHeight: 1.15, display: "inline-block" }}>{tag.data?.friendly_name || tag.name}</span>
                            )
                        }
                        {
                            (p.row.internal_tags || []).map((tag, idx) =>
                                <span key={`int-${tag.name}-${idx}`} style={{ fontSize: ".88rem", padding: "2px 4px 2px 4px", color: tag.source === "participant" ? "#55555b" : "black", background: tag.source === "participant" ? "#F0F0F3" : "#f8d7da", borderRadius: 3, lineHeight: 1.15, display: "inline-block" }}>{tag.data?.friendly_name || tag.name}</span>
                            )
                        }
                    </div>
                </Tooltip>
            ),
        },
        {
            field: 'tour-date',
            headerName: 'Trip Date',
            sortable: false,
            flex: 0.2,
            renderCell: (p) => (
                <Tooltip title={p.row.participant.tour.start_ts}>
                    <Typography fontWeight={500}>
                        {moment.utc(p.row.participant.start_ts).format('MMM DD, YYYY')}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'tour-end-date',
            headerName: 'Trip End Date',
            sortable: false,
            flex: 0.2,
            renderCell: (p) => (
                <Tooltip title={p.row.participant.tour.end_ts}>
                    <Typography fontWeight={500}>
                        {moment.utc(p.row.participant.end_ts).format('MMM DD, YYYY')}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'name',
            headerName: 'Name',
            sortable: false,
            flex: 0.5,
            renderCell: (p) => (
                <Link style={{ cursor: 'pointer' }} onClick={() => history.push(`/user/${p.row.participant?.user?.id}`)}>
                    {render_user_name(p.row)}
                </Link>
            ),
        },
        {
            field: 'Review',
            headerName: 'Review',
            sortable: false,
            flex: 1,
            cellClassName: classes.reviewColumnCell,
            renderCell: (p) => {
                const commentAspect = p.row.data?.find(item => 
                    item.aspects?.some(aspect => aspect.type === "Comment" && aspect.comment)
                );
                const comment =  commentAspect?.aspects?.find(aspect => (aspect.type === "Comment" || aspect.type_id === 3))?.comment || p.row?.comment;
                let aspects = p.row.data?.flatMap(item => item.aspects || []);
                
                if(!aspects || aspects?.length === 0){
                    aspects = comment ? [{type: 'Comment', comment: comment}] : [];
                }
                const hasRatingAspects = (aspects || []).some((a) => a.type === 'Rating');
                const showSeeMore = hasRatingAspects || (aspects && aspects.length > 1);
                const reviewLink = positiveReviewLink(p.row, reviewThreshold);
                return (
                    <Box
                        sx={{
                            minWidth: 0,
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 0.5,
                            py: 0.5,
                        }}
                    >
                        {comment != null && comment !== '' && (
                            <Typography
                                component="div"
                                variant="body2"
                                sx={{ whiteSpace: 'normal', wordBreak: 'break-word', width: '100%' }}
                            >
                                {comment}
                            </Typography>
                        )}
                        {showSeeMore && (
                            <WhiteBackgroundTooltip title={<CustomTooltip aspects={aspects} />} placement="right">
                                <Typography color="primary" sx={{ cursor: 'pointer', flexShrink: 0 }}>See more</Typography>
                            </WhiteBackgroundTooltip>
                        )}
                        {reviewLink && (
                            <Typography color="primary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {reviewLink.direct ? 'Redirected to' : 'Review link:'} {reviewLink.url}
                            </Typography>
                        )}
                    </Box>
                );
            },
        },

        {
            field: 'buttons',
            headerName: 'Action',
            sortable: false,
            width: 75,
            renderCell: (p) => {
                if (!p.row.associated_twilio_conversation_unique_name || p.row.associated_twilio_conversation_non_guest_participants_state === "deleted") {
                    return <></>;
                }
                let chatChannelID = p.row.associated_twilio_conversation_unique_name?.split('::')[1];
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <a href={'/chat/' + chatChannelID + '/conversations/' + p.row.associated_twilio_conversation_unique_name + '/interactivechat'}>
                            <GoToChatIcon style={{ cursor: 'pointer' }} />
                        </a>
                    </div>
                );
            },
        },
    ];
    if(isModalOpen){
        return <ReviewModal open={isModalOpen} onClose={handleCloseModal} aspects={selectedAspects} />;
    }
    return (
        <div className={classes.root}>
            {/* Rendered as the "Ratings" tab of the Analytics page, so the browser
                tab reads "Analytics" like every other tab there (CP-7101). */}
            <PageMeta title="Analytics" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {confirmationDialogAction !== 'delete_review' && confirmationDialogAction !== 'duplicate_review' && ConfirmationDialogText[confirmationDialogAction].body}
                    {confirmationDialogAction === 'delete_review' && <Typography>{ConfirmationDialogText[confirmationDialogAction].body}</Typography>}
                </ConfirmationDialog>
            ) : null}
            <FixedLinearProgress visibility={syncActive || listLoading} />
            <Grid item xs={12}>
                <Grid item xs={12} p={isSmallScreen && 2} rb={0}>
                    <ReviewListToolbar
                        resource={resource} 
                        query={query} 
                        setRecordListFilters={setRecordListFilters} 
                        setQuery={setQuery} 
                        selectedIds={selectedIds} 
                        isSmallScreen={isSmallScreen} 
                        roleMap={roleMap} 
                        recordListFilters={recordListFilters} 
                        setLastUpdate={setLastUpdate} 
                        setSyncActive={setSyncActive}
                        onExportList={onExportList}
                        showPeriod={showInsights}
                        period={period}
                        setPeriod={setPeriod}
                    />
                </Grid>
                {showInsights && (
                    <Grid item xs={12}>
                        <ReviewInsights
                            filters={chartFilters}
                            query={query}
                            range={periodRangeValue}
                            periodKey={period}
                            configuredScale={reviewRatingSystem}
                            threshold={reviewThreshold}
                        />
                    </Grid>
                )}
                <Grid item xs={12} className={classes.recordListWrapper}>
                    <RecordList
                        className={clsx(
                            classes.recordList,
                            listLoading && (listAnswered ? classes.recordListReloading : classes.recordListFirstLoad)
                        )}
                        resource={resource}
                        disableSelectionOnClick={true}
                        apiFilters={listFilters}
                        query={query}
                        sortModel={[{ field: 'name', sort: 'asc' }]}
                        deps={lastUpdate}
                        disableColumnFilter={true}
                        isSmallScreen={isSmallScreen}
                        listItemRenderer={ListItemRenderer}
                        checkboxSelection={false}
                        disablePageUrlData={true}
                        rowHeight={125}
                        relatedLoading={relatedLoading}
                        setIsLoading={onListLoading}
                        slots={REVIEW_SLOTS}
                        getRowHeight={() => 'auto'}
                        columns={record_list_cols}
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default ReviewList;
